/**
 * Data processor for the `wsolution_calls` Elasticsearch config query.
 *
 * Shapes the aggregation envelope into a flat, render-ready `rows` array so the
 * WSolution home page can render its table 100% declaratively in YAML — no
 * presentational React component required. All client-side concerns that YAML
 * can't express (wsid label, date/number formatting, the per-row status-family
 * proportion bar and the action hrefs carrying the page's `id` query param) are
 * baked here.
 *
 * Matches the config-query endpoint by URL and augments the response with
 * `rows`, leaving the rest of the envelope untouched.
 */

import { isIncomingWsid, othersLabel, wsidHost, wsolutionHrefs } from './wsolutionWsid';

// HTTP status family → colour (hex, for the inline-styled proportion bar).
const FAMILY_COLORS = { 2: '#10b981', 3: '#3b82f6', 4: '#f59e0b', 5: '#ef4444' };

function formatDate(iso) {
    if (!iso) return '—';
    try {
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        }).format(new Date(iso));
    } catch {
        return String(iso);
    }
}

/**
 * Groups a row's per-code buckets into 2xx/3xx/4xx/5xx families. Returns both the
 * presentational `segments` of a proportion bar (each carries a ready-to-bind
 * inline `style` — width % + colour — and a tooltip `title`) and the raw
 * numeric data (`counts` per family, `total`, `dominant` family) the ReactFlow
 * graph needs to size/colour its edges without re-parsing the formatted strings.
 */
function buildFamilyData(statusBuckets) {
    const counts = {};
    let total = 0;
    for (const s of statusBuckets) {
        const fam = Math.floor(Number(s.key) / 100);
        const c = s.status_count?.value ?? s.doc_count ?? 0;
        counts[fam] = (counts[fam] ?? 0) + c;
        total += c;
    }

    const segments = [2, 3, 4, 5]
        .filter((f) => counts[f] > 0)
        .map((f) => {
            const count = counts[f];
            const pct = total ? (count / total) * 100 : 0;
            return {
                key: `${f}xx`,
                style: { width: `${pct}%`, backgroundColor: FAMILY_COLORS[f] },
                title: `${f}xx — ${count.toLocaleString('fr-FR')} (${pct.toFixed(1)} %)`,
            };
        });

    // Dominant family = the one with the most calls; drives the edge colour.
    let dominant = null;
    let max = -1;
    for (const f of [2, 3, 4, 5]) {
        if ((counts[f] ?? 0) > max) { max = counts[f] ?? 0; dominant = f; }
    }

    return { segments, counts, total, dominant };
}

// Combien d'appelants et de chemins le graphe montre avant de regrouper le
// reste : au-delà, une colonne de nœuds ne se lit plus.
const MAX_CALLERS = 6;
const MAX_PATHS = 6;
const OTHER_CALLERS = '__autres_appelants__';
const OTHER_PATHS = '__autres_chemins__';

/**
 * Les appelants listés par une agrégation : l'URL référente quand l'appel en
 * annonce une, l'adresse IP sinon.
 *
 * La règle est tranchée par la REQUÊTE : `by_initiator_referer` groupe les
 * appels qui ont un référent, `without_referer.by_initiator_addr` groupe par
 * adresse ceux qui n'en ont pas. Les deux ensembles sont disjoints par
 * construction, donc chaque appel est compté une fois et une seule (une
 * agrégation par IP tout court compterait deux fois les appels avec référent).
 */
function callerBuckets(owner) {
    return [
        ...(owner?.by_initiator_referer?.buckets ?? [])
            .map((b) => ({ key: `referer:${b.key}`, label: String(b.key), kind: 'referer', count: b.doc_count ?? 0 })),
        ...(owner?.without_referer?.by_initiator_addr?.buckets ?? [])
            .map((b) => ({ key: `addr:${b.key}`, label: String(b.key), kind: 'addr', count: b.doc_count ?? 0 })),
    ].filter((c) => c.count > 0);
}

const callerTitle = (c) => ({
    referer: `Appels venant de ${c.label}`,
    addr: `Appels de ${c.label}, sans référent annoncé`,
    other: `Appelants hors des ${MAX_CALLERS} premiers`,
}[c.kind]);

/**
 * Le flux d'un webservice ENTRANT : appelants → chemins appelés → site.
 *
 * Côté entrant, l'hôte de destination est toujours le nôtre : ce qui se lit,
 * c'est QUI appelle (référent, ou IP) et QUOI (le chemin : `/api/route`). Les
 * chemins sont l'ancre, parce que ce sont eux qui rejoignent le site ; chaque
 * chemin porte la ventilation de ses appelants (sous-agrégations de `by_path`),
 * d'où les arêtes appelant → chemin.
 *
 * Les deux colonnes sont bornées (MAX_CALLERS, MAX_PATHS) et leur reste est
 * regroupé plutôt qu'omis, sans quoi les épaisseurs mentiraient sur les
 * volumes : un appelant hors du top part vers « Autres appelants », un chemin
 * hors du top vers « Autres chemins », et ce qu'on ne sait pas attribuer à un
 * appelant nommé retombe sur « Autres appelants » — jamais sur un appelant
 * qu'on aurait deviné.
 *
 * @return {{callers: object[], paths: object[], flows: object[]}|null}
 *   Null quand le serveur n'a rien renvoyé à déplier.
 */
function buildIncomingFlow(bucket, total, linkTo) {
    const shown = callerBuckets(bucket).sort((a, b) => b.count - a.count).slice(0, MAX_CALLERS);
    const shownKeys = new Set(shown.map((c) => c.key));

    const allPaths = (bucket.by_path?.buckets ?? []).filter((p) => (p.doc_count ?? 0) > 0);
    const pathBuckets = allPaths.slice(0, MAX_PATHS);
    const otherPaths = (bucket.by_path?.sum_other_doc_count ?? 0)
        + allPaths.slice(MAX_PATHS).reduce((s, p) => s + p.doc_count, 0);

    if (!shown.length && !pathBuckets.length) return null;

    const flows = new Map();
    const add = (caller, path, n) => {
        if (n <= 0) return;
        const k = JSON.stringify([caller, path]);
        flows.set(k, { caller, path, count: (flows.get(k)?.count ?? 0) + n });
    };
    const flowOf = (caller, path) => flows.get(JSON.stringify([caller, path]))?.count ?? 0;

    for (const p of pathBuckets) {
        const pathKey = `path:${p.key}`;
        let attributed = 0;
        for (const c of callerBuckets(p)) {
            add(shownKeys.has(c.key) ? c.key : OTHER_CALLERS, pathKey, c.count);
            attributed += c.count;
        }
        // Queue tronquée de la sous-agrégation : on ne sait pas QUI, donc
        // « Autres appelants », pas le plus gros appelant par défaut.
        add(OTHER_CALLERS, pathKey, (p.doc_count ?? 0) - attributed);
    }

    if (otherPaths > 0) {
        let routed = 0;
        for (const c of shown) {
            const known = pathBuckets.reduce((s, p) => s + flowOf(c.key, `path:${p.key}`), 0);
            const n = Math.max(0, c.count - known);
            add(c.key, OTHER_PATHS, n);
            routed += n;
        }
        add(OTHER_CALLERS, OTHER_PATHS, otherPaths - routed);
    }

    // Chaque carte mène au tableau de bord ou aux logs PRÉFILTRÉS sur ce qu'elle
    // représente : le référent, ou l'IP (qui vaut « cette adresse, SANS référent »,
    // exactement comme le nœud la compte). Les nœuds « Autres », eux, n'ont pas de
    // critère exprimable : ils ouvrent le webservice entier.
    const callers = shown.map((c) => ({
        ...c,
        countFormatted: c.count.toLocaleString('fr-FR'),
        title: callerTitle(c),
        ...linkTo(c.kind === 'referer' ? { referer: c.label } : { addr: c.label }),
    }));
    const otherOut = [...flows.values()]
        .filter((f) => f.caller === OTHER_CALLERS)
        .reduce((s, f) => s + f.count, 0);
    const otherCount = Math.max(total - shown.reduce((s, c) => s + c.count, 0), otherOut);
    if (otherCount > 0) {
        // Avec des milliers d'IP, ce nœud en cache la plupart : il dit COMBIEN
        // (cardinalités serveur des référents et des adresses sans référent, deux
        // ensembles disjoints), pas seulement qu'il en reste.
        const distinct = bucket.callers_referer_distinct?.value == null && bucket.without_referer?.distinct?.value == null
            ? null
            : (bucket.callers_referer_distinct?.value ?? 0) + (bucket.without_referer?.distinct?.value ?? 0);
        const label = othersLabel(distinct, shown.length, ['appelant', 'appelants']) ?? 'Autres appelants';
        const other = { key: OTHER_CALLERS, label, kind: 'other', count: otherCount };
        callers.push({ ...other, countFormatted: otherCount.toLocaleString('fr-FR'), title: callerTitle(other), ...linkTo() });
    }

    const paths = pathBuckets.map((p) => {
        const count = p.doc_count ?? 0;
        const avgMs = Math.round((p.avg_response_time_raw?.value ?? 0) / 1000);
        const family = buildFamilyData(p.by_http_status_code?.buckets ?? []);
        return {
            key: `path:${p.key}`,
            label: String(p.key),
            count,
            countFormatted: count.toLocaleString('fr-FR'),
            avgFormatted: `${avgMs.toLocaleString('fr-FR')} ms`,
            families: family.segments,
            familyCounts: family.counts,
            title: `Appels reçus sur ${p.key}`,
            ...linkTo({ path: String(p.key) }),
        };
    });
    if (otherPaths > 0) {
        paths.push({
            key: OTHER_PATHS,
            label: othersLabel(bucket.paths_distinct?.value, pathBuckets.length, ['chemin', 'chemins']) ?? 'Autres chemins',
            muted: true,
            count: otherPaths,
            countFormatted: otherPaths.toLocaleString('fr-FR'),
            avgFormatted: '—',
            families: [],
            familyCounts: {},
            title: `Chemins hors des ${MAX_PATHS} premiers`,
            ...linkTo(),
        });
    }

    return { callers, paths, flows: [...flows.values()] };
}

export const wsolutionCallsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
    if (!requestContext?.url?.includes('/config/wsolution_calls')) return dataToProcess;
    if (responseContext?.status >= 400) return dataToProcess;
    if (!dataToProcess || typeof dataToProcess !== 'object') return dataToProcess;

    const nodeId = new URLSearchParams(window.location.search).get('id') ?? '';
    // Sort webservices by most recent call first (the "Dernier appel" column).
    const buckets = [...(dataToProcess?.aggregations?.by_passeplat_wsid?.buckets ?? [])]
        .sort((a, b) => (b.last_record_date?.value ?? 0) - (a.last_record_date?.value ?? 0));

    const rows = buckets.map((b) => {
        const count = b.count?.value ?? b.doc_count ?? 0;
        const avgMs = Math.round((b.avg_response_time_raw?.value ?? 0) / 1000);
        const wsid = b.key;
        const enc = encodeURIComponent(wsid);
        const family = buildFamilyData(b.by_http_status_code?.buckets ?? []);
        // Sens de l'appel. Un webservice entrant, c'est le site lui-même comme
        // destination : la flèche du graphe s'inverse et les écrans basculent
        // leur axe sur l'appelant (cf. wsolutionWsid.js).
        const incoming = isIncomingWsid(wsid);
        // Liens vers le tableau de bord et les logs de CE webservice, avec un
        // préfiltre éventuel (chemin, référent, IP) que ces deux pages lisent dans l'URL.
        const linkTo = (prefilter = {}) => wsolutionHrefs({ id: nodeId, wsid, ...prefilter });
        return {
            wsid,
            label: wsidHost(wsid),
            incoming,
            directionLabel: incoming ? 'Entrant' : 'Sortant',
            directionClass: incoming
                ? 'inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-sky-100 text-sky-700'
                : 'inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600',
            directionTitle: incoming
                ? 'Appels entrants : le site est la destination'
                : 'Appels sortants : le site est l’appelant',
            count,                       // raw total, for the graph edge thickness
            avgMs,                       // raw average (ms), for the graph node
            countFormatted: count.toLocaleString('fr-FR'),
            lastCall: formatDate(b.last_record_date?.value_as_string),
            avgFormatted: `${avgMs.toLocaleString('fr-FR')} ms`,
            families: family.segments,
            familyCounts: family.counts,     // { 2: n, 3: n, … } raw, for edge colour
            dominantFamily: family.dominant, // 2 | 3 | 4 | 5 — drives edge colour
            // Appelants → chemins appelés, dans le seul sens entrant.
            incomingFlow: incoming ? buildIncomingFlow(b, count, linkTo) : null,
            logsHref: `/wsolution/logs?id=${nodeId}&wsid=${enc}`,
            dashboardHref: `/wsolution/dashboard?id=${nodeId}&wsid=${enc}`,
        };
    });

    return { ...dataToProcess, rows };
};
